import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Save, Eye, EyeOff, RefreshCw, Quote as QuoteIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const IntroQuotesTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newText, setNewText] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [busyId, setBusyId] = useState(null);

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/intro-quotes`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load quotes');
      setItems(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newText.trim() || !newAuthor.trim()) return;
    setBusyId('new');
    try {
      const res = await fetch(`${API}/api/admin/intro-quotes`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: newText.trim(),
          author: newAuthor.trim(),
          active: true,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Create failed');
      }
      setNewText('');
      setNewAuthor('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const patch = async (id, updates) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/intro-quotes/${id}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Update failed');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/intro-quotes/${id}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) throw new Error('Delete failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const updateField = (id, field, value) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
  };

  const activeCount = items.filter((x) => x.active).length;

  return (
    <div className="space-y-5" data-testid="intro-quotes-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <QuoteIcon className="h-4 w-4 text-yellow-500" />
            <h3 className="text-sm font-black tracking-widest text-yellow-500 uppercase">
              Intro Loader Quotes
            </h3>
          </div>
          <p className="text-sm text-gray-400">
            These quotes rotate through the terminal loader that visitors see once per session on{' '}
            <span className="text-yellow-400 font-bold">outlawmedia.com.au</span>.
            One is drawn at random per page-load. Currently{' '}
            <span className="text-emerald-400 font-bold">{activeCount} active</span> /{' '}
            <span className="text-gray-500">{items.length} total</span>.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="intro-quotes-refresh-btn"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Create form */}
      <form
        onSubmit={create}
        className="bg-zinc-900/80 backdrop-blur-xl border border-yellow-500/30 rounded-lg p-4 space-y-3"
        data-testid="intro-quotes-create-form"
      >
        <p className="text-xs font-black tracking-widest text-yellow-500 uppercase">Add Quote</p>
        <textarea
          required
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="e.g. The pen is mightier than the sword."
          data-testid="intro-quotes-new-text"
          maxLength={600}
          rows={3}
          className="w-full bg-black/50 border border-yellow-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-yellow-500 resize-none"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            required
            value={newAuthor}
            onChange={(e) => setNewAuthor(e.target.value)}
            placeholder="Author - e.g. Edward Bulwer-Lytton"
            data-testid="intro-quotes-new-author"
            maxLength={120}
            className="flex-1 bg-black/50 border border-yellow-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-yellow-500"
          />
          <button
            type="submit"
            disabled={busyId === 'new' || !newText.trim() || !newAuthor.trim()}
            data-testid="intro-quotes-add-btn"
            className="bg-yellow-500 hover:bg-yellow-400 text-black disabled:opacity-40 font-black px-4 py-2 rounded text-sm flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            ADD QUOTE
          </button>
        </div>
        <p className="text-[10px] text-gray-500 uppercase tracking-widest">
          Max 600 chars for the quote, 120 for the author. Line breaks are preserved in the loader.
        </p>
      </form>

      {error && (
        <div
          className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm"
          data-testid="intro-quotes-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="intro-quotes-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400"
          data-testid="intro-quotes-empty"
        >
          No quotes yet. Add one above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="intro-quotes-list">
          {items.map((q) => (
            <div
              key={q.id}
              data-testid={`intro-quote-item-${q.id}`}
              className={`bg-zinc-900/80 backdrop-blur-xl border rounded-lg p-3 space-y-2 ${
                q.active ? 'border-yellow-500/25' : 'border-zinc-700/50 opacity-60'
              }`}
            >
              <div className="flex items-start gap-2">
                <QuoteIcon className="h-4 w-4 text-yellow-500 mt-2.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <textarea
                    value={q.text}
                    onChange={(e) => updateField(q.id, 'text', e.target.value)}
                    data-testid={`intro-quote-text-${q.id}`}
                    maxLength={600}
                    rows={2}
                    className="w-full bg-black/50 border border-yellow-500/25 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-yellow-500 resize-none"
                  />
                  <input
                    type="text"
                    value={q.author}
                    onChange={(e) => updateField(q.id, 'author', e.target.value)}
                    data-testid={`intro-quote-author-${q.id}`}
                    maxLength={120}
                    className="w-full bg-black/50 border border-yellow-500/25 text-yellow-400 px-3 py-2 rounded text-xs uppercase tracking-widest font-bold focus:outline-none focus:border-yellow-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <button
                  onClick={() => patch(q.id, { active: !q.active })}
                  disabled={busyId === q.id}
                  data-testid={`intro-quote-toggle-${q.id}`}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-bold ${
                    q.active
                      ? 'bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-200'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-gray-400'
                  }`}
                >
                  {q.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {q.active ? 'ACTIVE' : 'HIDDEN'}
                </button>
                <button
                  onClick={() => patch(q.id, { text: q.text, author: q.author })}
                  disabled={busyId === q.id}
                  data-testid={`intro-quote-save-${q.id}`}
                  className="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-400 text-black disabled:opacity-50 font-black text-xs px-3 py-1.5 rounded"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save
                </button>
                <button
                  onClick={() => remove(q.id)}
                  disabled={busyId === q.id}
                  data-testid={`intro-quote-delete-${q.id}`}
                  className="flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 text-red-200 font-bold text-xs px-3 py-1.5 rounded"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IntroQuotesTab;
